/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* eslint-disable react/prop-types */
import ImportIcon from '@mui/icons-material/esm/CloudDownload';
import {
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	FormGroup,
	Typography
} from '@mui/material';
import { withStyles } from '@mui/styles';
import React, { useState } from 'react';
import Dropzone from 'react-dropzone';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as Actions from '../../actions/actions';
import { Overlay } from '../utils/Overlay';
import ConfirmDialog from './ConfirmDialog';

const AfterRestoreInfoDialog = (props) => {
	const { open } = props;
	return (
		<div>
			<Dialog open={open}>
				<DialogTitle>
					<FormattedMessage id="Database.SuccessTitle" defaultMessage="Restore Success" />
				</DialogTitle>
				<DialogContent
					style={{
						margin: '20px 10px 10px 5px'
					}}
				>
					<FormattedMessage
						id="Database.SuccessMessage"
						defaultMessage="Database restored successfully. Please restart Streamsheets to complete the process."
					/>
				</DialogContent>
			</Dialog>
		</div>
	);
};

function DatabaseComponent(props) {
	const [restoreFile, setRestoreFile] = useState(null);
	const [restoreState, setRestoreState] = useState('not-fetched');
	const [backupState, setBackupState] = useState('not-fetched');
	const dropzoneRef = React.createRef();

	const onRestoreFileSelected = (acceptedFiles) => {
		if (acceptedFiles.length === 1) {
			setRestoreFile(acceptedFiles[0]);
		} else if (acceptedFiles.length > 1) {
			// Only one file at a time can be restored
			props.showErrorDialog('Restore.Error.OnlyOneFile.Title', 'Restore.Error.OnlyOneFile.Message');
		}
	};

	// const handleDatabaseBackup = () => props.backup();
	const clearRestoreFile = () => setRestoreFile(null);

	const handleDatabaseRestore = async () => {
		try {
			setRestoreState('fetching');
			await props.restore(restoreFile);
			setRestoreState('fetched');
		} catch (error) {
			// TODO: Handle error
		}
		clearRestoreFile();
	};

	const onBackup = async () => {
		setBackupState('fetching');
		await props.backup();
		setBackupState('fetched');
	};

	return (
		<div>
			{restoreState === 'fetching' || backupState === 'fetching' ? (
				<Overlay>
					<CircularProgress style={{ width: '24px', height: '24px' }} />
				</Overlay>
			) : null}
			<AfterRestoreInfoDialog open={restoreState === 'fetched'} />
			<ConfirmDialog
				onConfirm={handleDatabaseRestore}
				onCancel={clearRestoreFile}
				open={!!restoreFile && restoreState === 'not-fetched'}
				title={<FormattedMessage id="Database.RestoreTitle" defaultMessage="Restore Database" />}
				content={
					<FormattedMessage
						id="Database.RestoreMessage"
						defaultMessage="Please confirm to restore the database"
						values={{
							restoreFileName: restoreFile && restoreFile.name
						}}
					/>
				}
			/>
			<Dropzone
				ref={dropzoneRef}
				disableClick
				accept="application/json"
				onDropAccepted={onRestoreFileSelected}
				style={{ width: '100%', height: '100%' }}
			>
				{({ isDragAccept }) => (
					<div style={{ padding: '12px' }}>
						{isDragAccept ? (
							<div
								style={{
									position: 'absolute',
									width: '100%',
									height: '100%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									backgroundColor: 'rgba(255, 255, 255, 0.3)',
									zIndex: 1
								}}
							>
								<ImportIcon
									style={{
										color: 'rgba(0, 0, 0, 0.7)',
										fontSize: '100pt'
									}}
								/>
							</div>
						) : null}
						<FormGroup
							style={{
								margin: '10px'
							}}
						>
							<div style={{ display: 'flex', flexDirection: 'row' }}>
								<FormControl
									style={{
										marginTop: '10px',
										width: '250px',
										marginBottom: '10px'
									}}
								>
									<Button variant="outlined" onClick={onBackup}>
										<FormattedMessage id="Database.Backup" defaultMessage="Backup database" />
									</Button>
								</FormControl>
								<Typography style={{ alignSelf: 'center', marginLeft: '20px' }}>
									<FormattedMessage id="Database.BackupHint" defaultMessage="Restore database " />
								</Typography>
							</div>
							<div style={{ display: 'flex', flexDirection: 'row' }}>
								<FormControl
									style={{
										marginTop: '10px',
										width: '250px',
										marginBottom: '10px'
									}}
								>
									<Button variant="outlined" onClick={() => dropzoneRef.current.open()}>
										<FormattedMessage id="Database.Restore" defaultMessage="Restore database" />
									</Button>
								</FormControl>
								<Typography style={{ alignSelf: 'center', marginLeft: '20px' }}>
									<FormattedMessage id="Database.RestoreHint" defaultMessage="Restore database " />
								</Typography>
							</div>
						</FormGroup>
					</div>
				)}
			</Dropzone>
		</div>
	);
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators({ ...Actions }, dispatch);
}

export const Database = injectIntl(
	connect(null, mapDispatchToProps)(withStyles({}, { withTheme: true })(DatabaseComponent))
);
